/**
 * useMapMarkers - Hook for converting trips to map/globe markers.
 *
 * Transforms a list of trips into GlobeMarker objects that can be
 * rendered on the globe surface or map.
 */

import { useMemo } from 'react'
import type { Trip as TripMessage } from '@generated/proto/model'
import type { GlobeMarker } from '@/domain/globe/types'
import { getGeocodedDestination } from '@/lib/trip-utils'

interface TripWithId {
  id: string
  proto: TripMessage
}

/**
 * Hook for converting trips to map markers.
 *
 * Extracts the first destination from each trip (if available) and
 * creates markers positioned at those locations. Trips without
 * destinations are skipped.
 *
 * @param trips - Array of trips with IDs and proto data
 * @returns Array of GlobeMarker objects
 *
 * @example
 * ```tsx
 * const markers = useMapMarkers(trips)
 * // Use markers in globe visualization
 * ```
 */
export function useMapMarkers(trips: TripWithId[]): GlobeMarker[] {
  return useMemo(() => {
    const markers: GlobeMarker[] = []

    for (const trip of trips) {
      // Get the first destination with valid coordinates
      const destination = getGeocodedDestination(trip.proto)

      if (destination?.location) {
        markers.push({
          id: `trip-${trip.id}`,
          position: {
            latitude: destination.location.latitude,
            longitude: destination.location.longitude,
          },
          type: 'trip',
          data: {
            tripId: trip.id,
            trip: trip.proto,
            destinationName: destination.name,
          },
        })
      }
    }

    return markers
  }, [trips])
}

/**
 * Get a single marker for a specific trip.
 * Useful when you need to highlight or focus on a particular trip's location.
 *
 * @param trip - Trip proto message
 * @param tripId - Trip ID
 * @returns GlobeMarker or null if trip has no destination with location
 */
export function getMapMarker(trip: TripMessage, tripId: string): GlobeMarker | null {
  const destination = getGeocodedDestination(trip)

  if (!destination?.location) return null

  return {
    id: `trip-${tripId}`,
    position: {
      latitude: destination.location.latitude,
      longitude: destination.location.longitude,
    },
    type: 'trip',
    data: {
      tripId,
      trip,
      destinationName: destination.name,
    },
  }
}
